import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Box, MapPin, Package, Tag } from "lucide-react";

import {
  useProduct,
  useProductAttributes,
  useProductIdentifiers,
  useProductMedia,
  useStockSummary,
  useTransactions,
} from "@/api/queries";
import type { StockPosition } from "@/api/types";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { ProductCapabilitiesCard } from "./ProductCapabilitiesCard";
import { ProductCommercialCard } from "./ProductCommercialCard";
import { ProductDocumentsCard } from "./ProductDocumentsCard";
import { ProductRelationsCard } from "./ProductRelationsCard";
import { ProductPhysicalCard } from "./ProductPhysicalCard";
import { ProductProvenanceCard } from "./ProductProvenanceCard";
import { TableShell, Td, Th, Tr } from "@/components/DataTable";
import { EmptyState, ErrorState, LoadingState, Skeleton } from "@/components/States";
import { Photo } from "@/components/Photo";
import { StatusBadge } from "@/components/StatusBadge";
import { stockLabel, stockTone } from "@/lib/stock";
import { PageHeader } from "@/layout/PageHeader";
import { Page } from "@/layout/Page";
import { MovementList } from "@/features/history/MovementList";
import { formatQuantity, formatUnit } from "@/lib/format";

const TABS = [
  { key: "overview", label: "Vue d'ensemble" },
  { key: "stock", label: "Stock par emplacement" },
  { key: "movements", label: "Mouvements" },
  { key: "photos", label: "Photos" },
  { key: "identifiers", label: "Identifiants" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

/**
 * The product sheet (TICKET FE-007).
 *
 * Stock by location is a first-class tab, never buried: the spec is explicit
 * that a product sheet must not hide where the thing actually is (§9).
 */
export function ProductDetailPage() {
  const { productId = "" } = useParams();
  const [tab, setTab] = useState<TabKey>("overview");

  const product = useProduct(productId, { enabled: Boolean(productId) });
  const stock = useStockSummary(productId, { enabled: Boolean(productId) });
  const media = useProductMedia(productId, { enabled: Boolean(productId) });

  if (product.isLoading) return <LoadingState label="Chargement du produit…" />;
  if (product.error) return <ErrorState error={product.error} onRetry={() => void product.refetch()} />;
  if (!product.data) return null;

  const item = product.data;
  // Une photo, pas n'importe quel fichier. Depuis que les fiches techniques se
  // déposent ici, un produit peut n'avoir que des PDF — et « le premier média »
  // affichait alors un PDF dans le cadre photo, qui restait vide.
  const photos = media.data?.filter((entry) => entry.mime_type.startsWith("image/"));
  const mainMedia = photos?.find((entry) => entry.media_type === "MAIN") ?? photos?.[0];

  return (
    <Page>
      <PageHeader
        crumbs={[
          { label: "Inventaire", to: "/inventory" },
          { label: item.name },
        ]}
        title={item.name}
        subtitle={[item.manufacturer, item.model].filter(Boolean).join(" · ") || undefined}
      />

      <div className="grid min-w-0 gap-3 @4xl:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-3">
          <Card>
            <CardBody className="flex flex-col gap-3">
              <Photo
                mediaId={mainMedia?.id ?? null}
                alt={item.name}
                className="aspect-square w-full"
                rounded="rounded-lg"
              />
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-[13px]">
                <Definition label="SKU" value={item.sku} mono />
                <Definition label="MPN" value={item.part_number} mono />
                <Definition label="Fabricant" value={item.manufacturer} />
                <Definition label="Modèle" value={item.model} />
                <Definition label="Unité" value={formatUnit(item.stock_unit)} />
                <Definition label="Statut" value={item.status} />
              </dl>
              {item.description && (
                <p className="border-t border-border pt-3 text-[13px] text-ink-muted">
                  {item.description}
                </p>
              )}
            </CardBody>
          </Card>

          <ProductPhysicalCard product={item} />

          <ProductCapabilitiesCard product={item} />

          <ProductCommercialCard product={item} />

          <ProductRelationsCard product={item} />

          <ProductDocumentsCard product={item} />

          <ProductProvenanceCard product={item} />

          <Card>
            <CardHeader title="Disponibilité" />
            <CardBody>
              {stock.isLoading ? (
                <Skeleton className="h-16 w-full" />
              ) : stock.data ? (
                <div className="grid grid-cols-3 gap-2 text-center">
                  <Figure
                    label="Disponible"
                    value={formatQuantity(stock.data.available_quantity)}
                    unit={formatUnit(stock.data.unit)}
                    emphasis
                  />
                  <Figure
                    label="Réservé"
                    value={formatQuantity(stock.data.reserved_quantity)}
                    unit={formatUnit(stock.data.unit)}
                  />
                  <Figure
                    label="Physique"
                    value={formatQuantity(stock.data.book_quantity)}
                    unit={formatUnit(stock.data.unit)}
                  />
                </div>
              ) : null}
              {stock.data && (
                <div className="mt-3 flex justify-center">
                  <StatusBadge
                    tone={stockTone(stock.data.available_quantity, stock.data.book_quantity)}
                  >
                    {stockLabel(stock.data.available_quantity, stock.data.book_quantity)}
                  </StatusBadge>
                </div>
              )}
              <p className="mt-3 border-t border-border pt-3 text-[11px] text-ink-subtle">
                Disponible = physique − réservé, calculé par le serveur à partir du ledger.
              </p>
            </CardBody>
          </Card>
        </div>

        <Card className="flex min-w-0 flex-col overflow-hidden">
          <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-border px-2 py-1.5">
            {TABS.map((entry) => (
              <button
                key={entry.key}
                type="button"
                onClick={() => setTab(entry.key)}
                className={
                  tab === entry.key
                    ? "whitespace-nowrap rounded-md bg-brand-subtle px-3 py-1.5 text-[13px] font-medium text-brand"
                    : "whitespace-nowrap rounded-md px-3 py-1.5 text-[13px] text-ink-muted hover:bg-surface-hover hover:text-ink"
                }
              >
                {entry.label}
              </button>
            ))}
          </div>

          <div className="min-w-0">
            {tab === "overview" && <OverviewTab productId={productId} />}
            {tab === "stock" && (
              <StockTab
                positions={stock.data?.positions ?? []}
                loading={stock.isLoading}
                unit={formatUnit(item.default_unit)}
              />
            )}
            {tab === "movements" && <MovementsTab productId={productId} />}
            {tab === "photos" && <PhotosTab productId={productId} />}
            {tab === "identifiers" && <IdentifiersTab productId={productId} />}
          </div>
        </Card>
      </div>
    </Page>
  );
}

function Definition({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-ink-subtle">{label}</dt>
      <dd className={mono ? "font-mono text-ink" : "text-ink"}>{value || "—"}</dd>
    </div>
  );
}

function Figure({
  label,
  value,
  unit,
  emphasis,
}: {
  label: string;
  value: string;
  unit: string;
  emphasis?: boolean;
}) {
  return (
    <div className="rounded-md border border-border bg-surface-sunken px-2 py-2.5">
      <p className="text-[11px] text-ink-subtle">{label}</p>
      <p
        className={
          emphasis
            ? "tnum text-xl font-semibold text-brand"
            : "tnum text-xl font-semibold text-ink"
        }
      >
        {value}
      </p>
      <p className="text-[10px] text-ink-subtle">{unit}</p>
    </div>
  );
}

function OverviewTab({ productId }: { productId: string }) {
  const attributes = useProductAttributes(productId);

  return (
    <CardBody className="flex flex-col gap-4">
      <section>
        <h3 className="mb-2 text-[13px] font-semibold text-ink">Attributs</h3>
        {attributes.isLoading && <Skeleton className="h-10 w-full" />}
        {attributes.data && attributes.data.length === 0 && (
          <p className="text-[13px] text-ink-subtle">
            Aucun attribut. Les caractéristiques (M18, béton, 18 V…) se saisissent ici plutôt
            que dans l'arbre des catégories.
          </p>
        )}
        {attributes.data && attributes.data.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {attributes.data.map((attribute) => (
              <li
                key={attribute.attribute_id}
                className="flex items-center gap-1.5 rounded-md border border-border bg-surface-sunken px-2 py-1 text-[12px]"
              >
                <Tag className="size-3 text-ink-subtle" aria-hidden />
                <span className="text-ink-muted">{attribute.name}</span>
                <span className="font-medium text-ink">
                  {Array.isArray(attribute.value)
                    ? attribute.value.join(", ")
                    : String(attribute.value ?? "—")}
                  {attribute.unit ? ` ${attribute.unit}` : ""}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

    </CardBody>
  );
}

function StockTab({
  positions,
  loading,
  unit,
}: {
  positions: StockPosition[];
  loading: boolean;
  unit: string;
}) {
  if (loading) return <LoadingState />;
  if (positions.length === 0) {
    return (
      <EmptyState
        icon={<MapPin className="size-6" aria-hidden />}
        title="Aucun stock enregistré"
        description="Recevez du stock pour voir apparaître les emplacements."
      />
    );
  }

  return (
    <TableShell>
      <thead>
        <tr>
          <Th>Emplacement</Th>
          <Th align="right">Physique</Th>
          <Th align="right">Réservé</Th>
          <Th align="right">Disponible</Th>
        </tr>
      </thead>
      <tbody>
        {positions.map((position) => (
          <Tr key={position.id}>
            <Td>
              {position.location_id ? (
                <Link
                  to={`/locations/${position.location_id}`}
                  className="flex items-center gap-1.5 text-ink hover:text-brand"
                >
                  <MapPin className="size-3.5 text-ink-subtle" aria-hidden />
                  Emplacement
                </Link>
              ) : position.container_id ? (
                <Link
                  to={`/containers/${position.container_id}`}
                  className="flex items-center gap-1.5 text-ink hover:text-brand"
                >
                  <Box className="size-3.5 text-ink-subtle" aria-hidden />
                  Contenant
                </Link>
              ) : (
                "—"
              )}
            </Td>
            <Td align="right" className="tnum">
              {formatQuantity(position.book_quantity)} {unit}
            </Td>
            <Td align="right" className="tnum text-ink-muted">
              {formatQuantity(position.reserved_quantity)}
            </Td>
            <Td align="right" className="tnum font-semibold">
              {formatQuantity(position.available_quantity)}
            </Td>
          </Tr>
        ))}
      </tbody>
    </TableShell>
  );
}

function MovementsTab({ productId }: { productId: string }) {
  const { data, isLoading, error, refetch } = useTransactions({ product_id: productId, limit: 50 });
  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  return <MovementList transactions={data?.items ?? []} />;
}

function PhotosTab({ productId }: { productId: string }) {
  const { data, isLoading } = useProductMedia(productId);
  if (isLoading) return <LoadingState />;
  if (!data || data.length === 0) {
    return (
      <EmptyState
        icon={<Package className="size-6" aria-hidden />}
        title="Aucune photo"
        description="Une photo évite un aller-retour à l'entrepôt : on reconnaît l'article avant d'aller le chercher."
      />
    );
  }
  return (
    <CardBody className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {data.map((entry) => (
        <figure key={entry.id} className="flex flex-col gap-1">
          <Photo mediaId={entry.id} alt={entry.original_filename ?? entry.media_type} className="aspect-square w-full" />
          <figcaption className="truncate text-[11px] text-ink-subtle">{entry.media_type}</figcaption>
        </figure>
      ))}
    </CardBody>
  );
}

function IdentifiersTab({ productId }: { productId: string }) {
  const { data, isLoading } = useProductIdentifiers(productId);
  if (isLoading) return <LoadingState />;
  if (!data || data.length === 0) {
    return (
      <EmptyState
        icon={<Tag className="size-6" aria-hidden />}
        title="Aucun identifiant"
        description="UPC, MPN, QR HD-Vault, code fournisseur : un article peut en porter plusieurs à la fois."
      />
    );
  }
  return (
    <TableShell>
      <thead>
        <tr>
          <Th>Type</Th>
          <Th>Valeur</Th>
          <Th className="hidden sm:table-cell">Émetteur</Th>
          <Th>Principal</Th>
        </tr>
      </thead>
      <tbody>
        {data.map((identifier) => (
          <Tr key={identifier.id}>
            <Td className="text-ink-muted">{identifier.identifier_type}</Td>
            <Td className="font-mono">{identifier.value}</Td>
            <Td className="hidden text-ink-muted sm:table-cell">{identifier.issuer || "—"}</Td>
            <Td>{identifier.is_primary && <StatusBadge tone="info">Principal</StatusBadge>}</Td>
          </Tr>
        ))}
      </tbody>
    </TableShell>
  );
}
